interface ScrollPosition {
  x: number;
  y: number;
  timestamp: number;
}

interface ScrollPositionStore {
  [key: string]: ScrollPosition;
}

const state = reactive<ScrollPositionStore>({});

/**
 * Save the current scroll position for a given route key
 * @param routeKey - Unique identifier for the route (usually the full path with query params)
 */
const saveScrollPosition = (routeKey: string) => {
  if (typeof window === "undefined") return;

  state[routeKey] = {
    x: window.scrollX,
    y: window.scrollY,
    timestamp: Date.now(),
  };
};

/**
 * Restore the scroll position for a given route key
 * @param routeKey - Unique identifier for the route
 * @param maxAge - Maximum age in milliseconds for the stored position (default: 5 minutes)
 */
const restoreScrollPosition = (routeKey: string, maxAge = 5 * 60 * 1000) => {
  if (typeof window === "undefined") return false;

  const savedPosition = state[routeKey];
  if (!savedPosition) return false;

  // Check if the saved position is too old
  if (Date.now() - savedPosition.timestamp > maxAge) {
    delete state[routeKey];
    return false;
  }

  // Use requestAnimationFrame to ensure DOM is ready
  requestAnimationFrame(() => {
    window.scrollTo({
      left: savedPosition.x,
      top: savedPosition.y,
      behavior: "auto", // Instant scroll for better UX on back navigation
    });
  });

  return true;
};

/**
 * Clear scroll position for a specific route key
 * @param routeKey - Unique identifier for the route
 */
const clearScrollPosition = (routeKey: string) => {
  delete state[routeKey];
};

/**
 * Clear all stored scroll positions
 */
const clearAllScrollPositions = () => {
  Object.keys(state).forEach((key) => delete state[key]);
};

/**
 * Get the stored scroll position for a route key
 * @param routeKey - Unique identifier for the route
 */
const getScrollPosition = (routeKey: string): ScrollPosition | undefined => {
  return state[routeKey];
};

/**
 * Check if a scroll position exists for a route key
 * @param routeKey - Unique identifier for the route
 */
const hasScrollPosition = (routeKey: string): boolean => {
  return !!state[routeKey];
};

/**
 * Composable for managing scroll position restoration across navigation
 * Particularly useful for preserving scroll position when navigating back from detail pages
 */
export const useScrollPosition = () => {
  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition,
    clearAllScrollPositions,
    getScrollPosition,
    hasScrollPosition,
  };
};
